/**
 * marketplaceConnectionAttention store — the count behind the Marketplace
 * "Connections" badge (held or still-charging connections: exactly the
 * Connections ledger's "Needs attention" tab, via the one shared
 * `connectionNeedsAttention` predicate) as one module-level cache (plan 443 M2).
 *
 * The page sidebar and the phone-width strip are both mounted on every
 * marketplace route (the sidebar is `hidden md:block`, not unmounted), and each
 * used to run its own `GET /api/marketplace/pro/connections`. Now one request
 * is in flight at a time and every reader shares its result.
 *
 * Mirrors `stores/marketplaceRequestList.ts`: a mounted consumer attaches and
 * loads what is missing; `bumpMarketplaceRequests` refetches once while a
 * consumer is mounted (a confirm creates a charging connection), otherwise
 * marks the cache stale; a bump landing during a fetch of an older version
 * chains exactly one follow-up. A failed fetch degrades to 0 (no badge).
 */
import { create } from "zustand";
import type { GetProToken } from "../hooks/useProToken";
import { listMarketplaceProConnections } from "../lib/api";
import { connectionNeedsAttention } from "../lib/marketplace-display";
import { useMarketplaceRequestsRefreshStore } from "./marketplaceRequests";

/** A cache older than this is refetched when a consumer attaches. */
export const CONNECTION_ATTENTION_STALE_MS = 60_000;

export interface ConnectionAttentionConsumer {
  getToken: GetProToken;
}

interface ConnectionAttentionState {
  count: number;
  /** The bump version whose fetch last resolved or failed (null = none yet). */
  loadedVersion: number | null;
  /** `Date.now()` of the last successful fetch. */
  fetchedAt: number | null;
  /** Invalidated, or the last fetch failed: the next attach refetches. */
  stale: boolean;
  /** Register a mounted consumer; returns its detach. Loads if due. */
  attach: (consumer: ConnectionAttentionConsumer) => () => void;
  reset: () => void;
}

const consumers = new Set<ConnectionAttentionConsumer>();
let inFlight: { promise: Promise<void>; version: number } | null = null;
// Bumped by reset() so a fetch that outlives it cannot write into fresh state.
let generation = 0;

function currentVersion(): number {
  return useMarketplaceRequestsRefreshStore.getState().version;
}

function anyToken(): GetProToken {
  const [first] = consumers;
  return first ? first.getToken : () => Promise.resolve(null);
}

function startLoad(getToken: GetProToken): Promise<void> {
  const gen = generation;
  const version = currentVersion();
  const run = (async () => {
    try {
      const rows = await listMarketplaceProConnections((await getToken()) ?? undefined);
      if (gen === generation) {
        useMarketplaceConnectionAttentionStore.setState({
          count: rows.filter((r) => connectionNeedsAttention(r.status)).length,
          loadedVersion: version,
          fetchedAt: Date.now(),
          stale: false,
        });
      }
    } catch {
      // Best-effort — a failed fetch just leaves the badge hidden.
      if (gen === generation) {
        useMarketplaceConnectionAttentionStore.setState({
          count: 0,
          loadedVersion: version,
          stale: true,
        });
      }
    }
  })();
  const entry = {
    version,
    promise: run.finally(() => {
      if (inFlight === entry) inFlight = null;
    }),
  };
  inFlight = entry;
  return entry.promise;
}

/** Shares the one in-flight read; a read for an older bump is followed by one fresh read. */
function loadConnectionAttention(getToken: GetProToken): Promise<void> {
  if (inFlight) {
    if (inFlight.version === currentVersion()) return inFlight.promise;
    return inFlight.promise.then(() => loadConnectionAttention(getToken));
  }
  return startLoad(getToken);
}

export const useMarketplaceConnectionAttentionStore = create<ConnectionAttentionState>(
  (set, get) => ({
    count: 0,
    loadedVersion: null,
    fetchedAt: null,
    stale: false,

    attach: (consumer) => {
      consumers.add(consumer);
      const { loadedVersion, fetchedAt, stale } = get();
      const due =
        stale ||
        loadedVersion !== currentVersion() ||
        fetchedAt === null ||
        Date.now() - fetchedAt > CONNECTION_ATTENTION_STALE_MS;
      if (due) void loadConnectionAttention(consumer.getToken);
      return () => {
        consumers.delete(consumer);
      };
    },

    reset: () => {
      generation += 1;
      consumers.clear();
      inFlight = null;
      set({ count: 0, loadedVersion: null, fetchedAt: null, stale: false });
    },
  }),
);

useMarketplaceRequestsRefreshStore.subscribe((state, prev) => {
  if (state.version === prev.version) return;
  if (consumers.size > 0) void loadConnectionAttention(anyToken());
  else useMarketplaceConnectionAttentionStore.setState({ stale: true });
});
